import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { SEO, getServiceSchema, getFAQSchema } from '../../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Home, CheckCircle, ArrowRight, Phone, Heart, Users, Baby } from 'lucide-react';

export const AdoptionSupportPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "At what stage of adoption should we consider counseling?",
      answer: "Any stage! Some families benefit from pre-adoption counseling to prepare, others seek support during the early adjustment period, and many come years after placement when new challenges arise. There's no 'right' time—support is valuable whenever you need it."
    },
    {
      question: "Do you work with all types of adoptions?",
      answer: "Yes. I work with families across all adoption types—domestic, international, transracial, foster care adoption, infant adoption, and older child adoption. Each has unique considerations, and I tailor my approach to your specific situation and needs."
    },
    {
      question: "How do I talk to my child about adoption?",
      answer: "This is one of the most common concerns. I provide guidance on age-appropriate ways to discuss adoption, helping you build a foundation of openness and trust. The conversation evolves as your child grows, and I can support you at each developmental stage."
    },
    {
      question: "Is adoption counseling available via telehealth in Hawaii?",
      answer: "Yes! Telehealth works very well for adoption support. Families throughout Hawaii can access specialized adoption-competent care from home. This is especially helpful for families who may not have local access to adoption-informed therapists."
    },
    {
      question: "What if my child has attachment issues?",
      answer: "Attachment challenges are common in adoption, especially with older child adoption or foster care adoption. I'm trained in attachment-focused interventions and can help your family build secure attachments through evidence-based approaches like Trust-Based Relational Intervention (TBRI)."
    },
    {
      question: "Can you help with open adoption relationships?",
      answer: "Yes. Open adoption relationships can be wonderful but also complex. I can help navigate boundaries, communication with birth families, and managing the unique dynamics of open adoption to ensure healthy relationships for everyone involved, especially your child."
    }
  ];

  const serviceSchema = getServiceSchema(
    "Adoption Support Counseling",
    "Specialized adoption counseling in Hawaii. Expert support for adoptive families navigating attachment, identity, and family dynamics with adoption-competent care.",
    "https://aloha-parents.preview.emergentagent.com/services/adoption-support"
  );

  const faqSchema = getFAQSchema(faqs);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema]
  };

  const adoptionChallenges = [
    "Attachment and bonding difficulties",
    "Identity and belonging questions",
    "Processing adoption-related grief and loss",
    "Behavioral challenges",
    "School and academic issues",
    "Communication within adoptive families",
    "Trauma history and its effects",
    "Transracial or transcultural adoption issues",
    "Birth family connections and questions",
    "Sibling relationships in adoptive families"
  ];

  const whoWeHelp = [
    {
      title: "Adoptive Parents",
      description: "Navigate the unique challenges of adoptive parenting with expert guidance and support.",
      icon: Heart
    },
    {
      title: "Adopted Children & Teens",
      description: "Help children and adolescents process their adoption story and build healthy identity.",
      icon: Baby
    },
    {
      title: "Adoptive Families",
      description: "Strengthen family bonds and improve communication within the adoptive family system.",
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <SEO 
        title="Adoption Support & Counseling in Hawaii"
        description="Specialized adoption counseling in Wailuku, HI. Expert support for adoptive families navigating attachment, identity, and challenges. All adoption types welcome."
        keywords="adoption counseling Hawaii, adoption support Maui, adoption therapist Wailuku, foster care adoption therapy, transracial adoption support"
        canonicalUrl="https://aloha-parents.preview.emergentagent.com/services/adoption-support"
        schemaData={combinedSchema}
      />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-center text-sm text-gray-600">
          <button onClick={() => navigate('/')} className="hover:text-teal-600 transition-colors">
            Home
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <button onClick={() => navigate('/#services')} className="hover:text-teal-600 transition-colors">
            Services
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Adoption Support</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-rose-50 via-pink-50 to-white py-16 px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mb-6">
                <Home className="w-8 h-8 text-rose-600" />
              </div>
              <Badge className="mb-4 bg-rose-100 text-rose-800">Specialized Adoption Counseling</Badge>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Adoption Support & Counseling in Hawaii
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Adoption is a beautiful journey that comes with unique challenges. I provide specialized 
                counseling for adoptive families, helping you navigate attachment, identity, and family 
                dynamics with compassionate, adoption-competent care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-rose-600 hover:bg-rose-700 text-white px-8 py-6 text-lg transition-all duration-200"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:8084707650'}
                  className="border-2 border-rose-600 text-rose-600 hover:bg-rose-50 px-8 py-6 text-lg transition-all duration-200"
                >
                  Call (808) 470-7650
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&q=80" 
                alt="Adoptive family"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Supporting Adoptive Families Through Every Stage
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Adoption creates families through love and commitment, but it also comes with unique 
                emotional and psychological considerations. As a therapist specializing in adoption 
                support, I understand the complexities that adoptive families face—from attachment 
                challenges and identity formation to trauma history and family dynamics.
              </p>
              <p>
                Whether you're preparing to adopt, in the early stages of building your family, or 
                navigating challenges years after placement, I provide adoption-competent counseling 
                that honors your family's unique story. My approach is rooted in attachment theory, 
                trauma-informed care, and a deep respect for the adoption triad—adoptive parents, 
                adopted individuals, and birth families.
              </p>
              <p>
                I work with adoptive parents to develop effective parenting strategies, help adopted 
                children and teens process their adoption story and build healthy identity, and support 
                the entire family in creating strong, secure attachments. Every adoption journey is 
                different, and I tailor my approach to your family's specific needs and circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Comprehensive Care</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Who We Support
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized counseling for every member of the adoptive family
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whoWeHelp.map((group, index) => {
              const IconComponent = group.icon;
              return (
                <Card key={index} className="border-none shadow-lg bg-white hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                      <IconComponent className="w-6 h-6 text-rose-600" />
                    </div>
                    <CardTitle className="text-2xl text-center">{group.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 text-center leading-relaxed">{group.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Common Adoption Challenges</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Issues We Address in Adoption Counseling
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Supporting families through the unique aspects of the adoption journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adoptionChallenges.map((challenge, index) => (
              <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-white to-rose-50 hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-rose-600 mr-3 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{challenge}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1609220136736-443140cffec6?w=800&q=80" 
                alt="Happy adoptive family"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <Badge className="mb-4 bg-green-100 text-green-800">My Approach</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Adoption-Competent Therapy
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                    <Heart className="w-5 h-5 text-rose-600 mr-2" />
                    Attachment-Focused Care
                  </h3>
                  <p className="text-gray-700">
                    Help build secure attachments between parents and children through Trust-Based 
                    Relational Intervention (TBRI) and attachment-focused strategies.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                    <Users className="w-5 h-5 text-rose-600 mr-2" />
                    Family Systems Approach
                  </h3>
                  <p className="text-gray-700">
                    Address how adoption impacts the entire family system, including siblings, 
                    extended family, and family dynamics.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                    <Baby className="w-5 h-5 text-rose-600 mr-2" />
                    Trauma-Informed Perspective
                  </h3>
                  <p className="text-gray-700">
                    Recognize and address the impact of early trauma, separation, and loss that 
                    many adopted children have experienced.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                    <Home className="w-5 h-5 text-rose-600 mr-2" />
                    Cultural Sensitivity
                  </h3>
                  <p className="text-gray-700">
                    Honor and support cultural identity, particularly for transracial and 
                    international adoptions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Parents */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-indigo-100 text-indigo-800">For Adoptive Parents</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Supporting You in Your Parenting Journey
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Pre-Adoption Preparation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Understanding attachment and bonding</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Preparing for potential challenges</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Building adoption competency</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Post-Adoption Support</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Addressing behavioral challenges</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Strengthening parent-child bonds</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Managing stress and self-care</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Identity & Belonging</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Talking about adoption with children</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Supporting cultural identity</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Navigating birth family connections</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Trauma & Healing</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Understanding adoption-related trauma</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Therapeutic parenting strategies</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Supporting emotional regulation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Hope Message */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-rose-50 to-pink-50">
        <div className="max-w-4xl mx-auto text-center">
          <Home className="w-16 h-16 text-rose-600 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Every Adoption Story is Unique
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Whether you've recently welcomed a child into your family or have been navigating adoption 
            for years, support is available. Adoption is a lifelong journey, and it's normal to need 
            guidance along the way. Together, we'll honor your family's unique story while building 
            the skills and connections that help everyone thrive.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">Common Questions</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Adoption Support Counseling FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem 
              value="item-1"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-rose-600 transition-colors">
                At what stage of adoption should we consider counseling?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Any stage! Some families benefit from pre-adoption counseling to prepare, others seek support 
                during the early adjustment period, and many come years after placement when new challenges arise. 
                There's no "right" time—support is valuable whenever you need it.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-2"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-rose-600 transition-colors">
                Do you work with all types of adoptions?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes. I work with families across all adoption types—domestic, international, transracial, foster 
                care adoption, infant adoption, and older child adoption. Each has unique considerations, and I 
                tailor my approach to your specific situation and needs.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-3"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-rose-600 transition-colors">
                How do I talk to my child about adoption?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                This is one of the most common concerns. I provide guidance on age-appropriate ways to discuss 
                adoption, helping you build a foundation of openness and trust. The conversation evolves as your 
                child grows, and I can support you at each developmental stage.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-4"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-rose-600 transition-colors">
                Is adoption counseling available via telehealth in Hawaii?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes! Telehealth works very well for adoption support. Families throughout Hawaii can access 
                specialized adoption-competent care from home. This is especially helpful for families who may 
                not have local access to adoption-informed therapists.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-5"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-rose-600 transition-colors">
                What if my child has attachment issues?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Attachment challenges are common in adoption, especially with older child adoption or foster care 
                adoption. I'm trained in attachment-focused interventions and can help your family build secure 
                attachments through evidence-based approaches like Trust-Based Relational Intervention (TBRI).
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-6"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-rose-600 transition-colors">
                Can you help with open adoption relationships?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes. Open adoption relationships can be wonderful but also complex. I can help navigate boundaries, 
                communication with birth families, and managing the unique dynamics of open adoption to ensure 
                healthy relationships for everyone involved, especially your child.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-rose-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Get the Support Your Adoptive Family Deserves
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Let's work together to build strong bonds and navigate your adoption journey with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-rose-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all duration-200 shadow-xl"
            >
              Schedule Consultation
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200"
            >
              Adoption Resources
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-80">
            Adoption-competent care • All adoption types welcome • Telehealth available
          </p>
        </div>
      </section>
    </div>
  );
};
